import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useToast } from "@/hooks/use-toast";
import { logEdgeFunctionError } from "@/utils/sentry";
import { supabase } from "@/integrations/supabase/client";
// DashboardSidebar and DashboardHeader are now in DashboardLayout
import { InstagramProfileSettings } from "@/components/settings/InstagramProfileSettings";
import { useLanguage } from "@/contexts/LanguageContext";
import { Eye, EyeOff, User, Mail, Lock, Building2, CreditCard, Loader2, ExternalLink, Sun, Moon, Pencil, AlertCircle } from "lucide-react";
import { useTheme } from "next-themes";
import { CategoryPicker } from "@/components/categories/CategoryPicker";
import {
  parseStoredCategory,
  serializeSelection,
  summarizeSelection,
} from "@/constants/categoryTaxonomy";

const Settings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscribed, tier, subscriptionEnd, cancelAtPeriodEnd } = useSubscription();
  const { toast } = useToast();
  const { t } = useLanguage();
  const { theme, setTheme } = useTheme();

  // Profile state
  const [userProfile, setUserProfile] = useState<any>(null);
  const [selectedSubcategoryIds, setSelectedSubcategoryIds] = useState<string[]>([]);
  const [legacyCategory, setLegacyCategory] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Email change state
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      const message = logEdgeFunctionError('customer-portal', err);
      toast({
        title: "Błąd",
        description: message,
        variant: "destructive",
      });
    } finally {
      setPortalLoading(false);
    }
  };

  // Fetch user profile on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching profile:', error);
          return;
        }

        if (data) {
          setUserProfile(data);
          const parsed = parseStoredCategory(data.category);
          setSelectedSubcategoryIds(parsed.subcategoryIds);
          setLegacyCategory(parsed.legacy);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchUserProfile();
  }, [user?.id]);

  const handleIndustryUpdate = async (subcategoryIds: string[]) => {
    if (!user?.id) {
      toast({
        title: t('common.error'),
        description: t('settings.notAuthenticated'),
        variant: "destructive",
      });
      return;
    }

    if (subcategoryIds.length === 0) {
      toast({
        title: t('common.error'),
        description: t('settings.selectIndustry'),
        variant: "destructive",
      });
      return;
    }

    const serialized = serializeSelection(subcategoryIds);
    setProfileLoading(true);

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          category: serialized,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: t('settings.industryUpdated'),
        description: t('settings.industryUpdatedDesc'),
      });

      setSelectedSubcategoryIds(subcategoryIds);
      setLegacyCategory(false);
      setUserProfile({ ...userProfile, category: serialized });
      setPickerOpen(false);
    } catch (error: any) {
      toast({
        title: t('settings.errorUpdatingIndustry'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newEmail) {
      toast({
        title: t('common.error'),
        description: t('settings.enterNewEmail'),
        variant: "destructive",
      });
      return;
    }

    setEmailLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) throw error;

      toast({
        title: t('settings.emailUpdateInitiated'),
        description: t('settings.emailUpdateInitiatedDesc'),
      });
      
      setNewEmail('');
    } catch (error: any) {
      toast({
        title: t('settings.errorUpdatingEmail'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: t('common.error'),
        description: t('settings.fillPasswordFields'),
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: t('common.error'),
        description: t('settings.passwordsDoNotMatch'),
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: t('common.error'),
        description: t('settings.passwordTooShort'),
        variant: "destructive",
      });
      return;
    }

    setPasswordLoading(true);

    try {
      // First verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: currentPassword,
      });

      if (signInError) {
        toast({
          title: t('common.error'),
          description: t('settings.currentPasswordIncorrect'),
          variant: "destructive",
        });
        return;
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast({
        title: t('settings.passwordUpdated'),
        description: t('settings.passwordUpdatedDesc'),
      });
      
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast({
        title: t('settings.errorUpdatingPassword'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <main className="flex-1 p-3 md:p-6 pb-16 flex justify-center overflow-y-auto">
      <div className="w-full max-w-[800px] space-y-4 md:space-y-6 pb-16">
          <div className="glass-card rounded-2xl p-6 md:p-8 border border-border/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t('settings.title')}</h1>
                <p className="text-sm text-muted-foreground mt-1">{t('settings.description')}</p>
              </div>
            </div>
          </div>

          {/* Instagram Profile Settings */}
          <InstagramProfileSettings />

          {/* Industry Selection Card */}
          <div className="glass-card rounded-2xl p-6 border border-border/50">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-xl font-bold">{t('settings.industryCategory')}</h3>
              </div>
              <p className="text-sm text-muted-foreground ml-12">
                {t('settings.selectYourIndustry')}
              </p>
            </div>
            <div className="ml-12 space-y-3">
              {legacyCategory && (
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="text-sm leading-relaxed">
                    {t('settings.legacyCategoryNotice')}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="group flex w-full items-center justify-between gap-3 rounded-xl border border-border bg-background/50 px-4 py-3 text-left transition-colors hover:border-primary/60 hover:bg-primary/5"
              >
                <div className="min-w-0">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('settings.industryCategory')}
                  </div>
                  <div className="mt-1 truncate text-sm font-medium text-foreground">
                    {selectedSubcategoryIds.length > 0
                      ? summarizeSelection(selectedSubcategoryIds)
                      : t('settings.industryNotSet')}
                  </div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors group-hover:border-primary/60 group-hover:text-primary">
                  <Pencil className="h-3.5 w-3.5" />
                  {selectedSubcategoryIds.length > 0
                    ? t('settings.editIndustry')
                    : t('settings.selectIndustry')}
                </span>
              </button>
            </div>
          </div>

          <Sheet open={pickerOpen} onOpenChange={setPickerOpen}>
            <SheetContent
              side="right"
              className="w-full sm:max-w-xl overflow-y-auto"
            >
              <SheetHeader className="mb-4">
                <SheetTitle>{t('settings.industryCategory')}</SheetTitle>
              </SheetHeader>
              <CategoryPicker
                initial={selectedSubcategoryIds}
                onSubmit={handleIndustryUpdate}
                onCancel={() => setPickerOpen(false)}
                isSaving={profileLoading}
                submitLabel={t('settings.saveChanges')}
              />
            </SheetContent>
          </Sheet>

          {/* Product Tour Card */}
          <div className="glass-card rounded-2xl p-6 border border-border/50">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold">{t('settings.productTour.title')}</h3>
              </div>
              <p className="text-sm text-muted-foreground ml-12">
                {t('settings.productTour.description')}
              </p>
            </div>
            <div className="ml-12 flex flex-wrap gap-3">
              <Button
                onClick={() => {
                  localStorage.setItem('product-tour-force', 'true');
                  // Clear both the legacy device-scoped key and the
                  // userId-scoped key, since the tour readers now look up
                  // the userId-scoped one for the current user.
                  localStorage.removeItem('product-tour-completed');
                  if (user?.id) {
                    localStorage.removeItem(`product-tour-completed:${user.id}`);
                  }
                  navigate('/panel');
                }}
                variant="outline"
                className="flex items-center gap-2"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t('settings.productTour.restart')}
              </Button>
              <Button
                onClick={() => {
                  localStorage.setItem('welcome_letter_force', 'true');
                  navigate('/panel');
                }}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Mail className="h-4 w-4" />
                Pokaż list powitalny
              </Button>
            </div>
          </div>


          <div className="glass-card rounded-2xl p-6 border border-border/50">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-xl font-bold">{t('settings.accountInfo')}</h3>
              </div>
              <p className="text-sm text-muted-foreground ml-12">
                {t('settings.viewAccountDetails')}
              </p>
            </div>
            <div className="space-y-4 ml-12">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">{t('settings.emailAddress')}</Label>
                <p className="text-foreground">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Subscription Management Card */}
          <div className="glass-card rounded-2xl p-6 border border-border/50">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-xl font-bold">Subskrypcja</h3>
              </div>
              <p className="text-sm text-muted-foreground ml-12">
                Zarządzaj swoim planem i płatnościami
              </p>
            </div>
            <div className="ml-12 space-y-4">
              {subscribed && tier ? (
                <>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center text-sm font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary capitalize">
                      Plan {tier === 'pro' ? 'Pro' : 'Starter'}
                    </span>
                    {subscriptionEnd && (
                      <span className="text-sm text-muted-foreground">
                        {cancelAtPeriodEnd
                          ? `Anulowany — aktywny do ${new Date(subscriptionEnd).toLocaleDateString('pl-PL')}`
                          : `Aktywny do ${new Date(subscriptionEnd).toLocaleDateString('pl-PL')}`
                        }
                      </span>
                    )}
                  </div>
                  <Button
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    {portalLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ExternalLink className="h-4 w-4" />
                    )}
                    Zarządzaj subskrypcją
                  </Button>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Nie masz aktywnej subskrypcji.
                </p>
              )}
            </div>
          </div>

          {/* Appearance Card */}
          <div className="glass-card rounded-2xl p-6 border border-border/50">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Sun className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-xl font-bold">Wygląd</h3>
              </div>
              <p className="text-sm text-muted-foreground ml-12">
                Wybierz motyw kolorystyczny aplikacji
              </p>
            </div>
            <div className="ml-12">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setTheme('light')}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    theme === 'light'
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border/50 text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  <Sun className="h-5 w-5" />
                  <span className="text-sm font-medium">Jasny</span>
                </button>
                <button
                  onClick={() => setTheme('dark')}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    theme === 'dark'
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border/50 text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  <Moon className="h-5 w-5" />
                  <span className="text-sm font-medium">Ciemny</span>
                </button>
              </div>
            </div>
          </div>

          <div className="pt-4">
            <div className="flex items-center gap-2 mb-4">
              <Separator className="flex-1" />
              <span className="text-sm font-medium text-muted-foreground">{t('settings.securitySettings')}</span>
              <Separator className="flex-1" />
            </div>
          </div>

          <div className="glass-card rounded-2xl p-6 border border-border/50">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-xl font-bold">{t('settings.changeEmail')}</h3>
              </div>
              <p className="text-sm text-muted-foreground ml-12">
                {t('settings.changeEmailDesc')}
              </p>
            </div>
            <div className="ml-12">
              <form onSubmit={handleEmailChange} className="space-y-4">
                <div>
                  <Label htmlFor="newEmail">{t('settings.newEmail')}</Label>
                  <Input
                    id="newEmail"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder={t('settings.enterNewEmailPlaceholder')}
                    className="mt-1"
                  />
                </div>
                <Button type="submit" disabled={emailLoading} className="w-full">
                  {emailLoading ? t('settings.updating') : t('settings.updateEmail')}
                </Button>
              </form>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-6 border border-border/50">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Lock className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-xl font-bold">{t('settings.changePassword')}</h3>
              </div>
              <p className="text-sm text-muted-foreground ml-12">
                {t('settings.changePasswordDesc')}
              </p>
            </div>
            <div className="ml-12">
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label htmlFor="currentPassword">{t('settings.currentPassword')}</Label>
                  <div className="relative mt-1">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder={t('settings.enterCurrentPasswordPlaceholder')}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <Separator />

                <div>
                  <Label htmlFor="newPassword">{t('settings.newPassword')}</Label>
                  <div className="relative mt-1">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t('settings.enterNewPasswordPlaceholder')}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">{t('settings.confirmPassword')}</Label>
                  <div className="relative mt-1">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t('settings.confirmPasswordPlaceholder')}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <Button type="submit" disabled={passwordLoading} className="w-full">
                  {passwordLoading ? t('settings.updating') : t('settings.updatePassword')}
                </Button>
              </form>
            </div>
          </div>
      </div>
    </main>
  );
};

export default Settings;